import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	type ElementRef,
	HostListener,
	signal,
	viewChildren,
	input,
	effect,
	viewChild,
	output,
} from '@angular/core';
import { createId } from '@core/util/file';
import {
	ClientSigningDetailsViewModelComplete,
	ESigningBlock,
	ESigningBlockType,
	HolisticPdfPageDetail,
	PdfPageDetail,
} from '@models/e-signing';
import { AVATAR_VARIANT_ORDER } from 'src/app/ui/utils/constants';
import { ESigningCanvasToolbarComponent } from './e-signing-canvas-toolbar/e-signing-canvas-toolbar.component';
import { IconsModule } from 'src/app/ui/icons/icons.module';
import { ESigningCanvasBlockComponent } from './e-signing-canvas-block/e-signing-canvas-block.component';

enum ActionType {
	DELETE = 'DELETE',
	ADD = 'ADD',
	UPDATE = 'UPDATE',
}

enum BlockChangeSource {
	MOVE = 'MOVE',
	RESIZE = 'resize',
	ADD = 'add',
	REDO = 'redo',
	UNDO = 'undo',
	DELETE = 'delete',
	INPUT = 'input',
}

interface ActiveBlock {
	event: DragEvent;
	block: ESigningBlock;
}

interface ESigningBlockHistory {
	block: ESigningBlock;
	action: ActionType;
}

export type ESigningBlockChange = {
	block: ESigningBlock;
	action: ActionType;
};

export type PushBlock = Pick<
	ESigningBlock,
	'elementType' | 'UIVariant' | 'height' | 'width' | 'clientId'
>;

@Component({
	selector: 'app-e-signing-canvas',
	imports: [
		CommonModule,
		ESigningCanvasBlockComponent,
		ESigningCanvasToolbarComponent,
		IconsModule,
	],
	templateUrl: './e-signing-canvas.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: { class: 'flex flex-col bg-gray-50 overflow-hidden' },
})
export class ESigningCanvasComponent {
	private MIN_BLOCK_WIDTH = 0.1;
	private MIN_BLOCK_HEIGHT = 0.1;
	private readonly PAGE_MARGIN = 4;
	public MAX_SCALE = 99.99;
	public MIN_SCALE = 0.1;
	private COLLISION_OFFSET = 20;
	private COLLISION_TOLERANCE = 1;
	public ESigningBlockType = ESigningBlockType;
	private UNDO_REVERSE_ACTIONS: Record<ActionType, ActionType> = {
		[ActionType.DELETE]: ActionType.ADD,
		[ActionType.ADD]: ActionType.DELETE,
		[ActionType.UPDATE]: ActionType.UPDATE,
	};
	private imageChildren_ = viewChildren<ElementRef<HTMLImageElement>>('page');
	private pdfContainer_ = viewChild<ElementRef<HTMLElement>>('pdfContainer');
	private draggedBlock_ = signal<ActiveBlock>(null);
	private copyBlock_ = signal<ESigningBlock>(null);
	private history_ = signal<ESigningBlockHistory[]>([]);
	private redo_ = signal<ESigningBlockHistory[]>([]);
	private focusedBlockId_ = signal<string | null>(null);
	private editingBlockId_ = signal<string | null>(null);
	public eSigningBlockChange = output<ESigningBlockChange>();
	public blockPlaced = output<void>();
	public pages = input<PdfPageDetail[]>(null);
	public eSigningBlocks = input<ESigningBlock[]>(null);
	public eSigningBlocks_ = signal<ESigningBlock[]>([]);
	public recipients = input<ClientSigningDetailsViewModelComplete[]>([]);
	public isLoading = input<boolean>(false);
	public recipientsIndex_ = computed(
		() =>
			new Map(
				this.recipients().map((recipient, index) => [recipient.id, index]),
			),
	);
	public eSigningBlocksRendered_ = computed(() => {
		const indexOfRecipient = this.recipientsIndex_();
		if (!indexOfRecipient.size || !this.eSigningBlocks_()?.length) return [];
		return this.eSigningBlocks_().map((block) => ({
			...block,
			UIVariant:
				AVATAR_VARIANT_ORDER[
					indexOfRecipient.get(block.clientId) % AVATAR_VARIANT_ORDER.length
				],
		}));
	});
	public scale_ = signal<number>(1);
	public percentage_ = computed(() => Math.round(this.scale_() * 100));
	public pages_ = computed<HolisticPdfPageDetail[]>(() =>
		this.setPages(this.pages(), this.eSigningBlocksRendered_()),
	);
	public pendingBlock = input<PushBlock>(null);
	public pendingDragBlock = input<PushBlock>(null);
	public maxPageWidth_ = computed(() => {
		if (!this.pages_().length) return 0;
		return Math.max(...this.pages_().map((p) => p.width), 0);
	});
	public readonly = input<boolean>(false);

	@HostListener('document:keydown.control.z', ['$event'])
	@HostListener('document:keydown.meta.z', ['$event'])
	private onUndo(event: KeyboardEvent) {
		if (this.readonly()) return;
		event.preventDefault();
		if (!this.history_().length) return;
		const lastEvent = this.history_().at(-1);
		this.history_.update((h) => h.slice(0, -1));
		if (!lastEvent) return;
		const currentBlock = this.eSigningBlocks_().find(
			(b) => b.id === lastEvent.block.id,
		);
		this.redo_.update((r) =>
			r.concat(
				currentBlock
					? { block: currentBlock, action: lastEvent.action }
					: lastEvent,
			),
		);
		this.updateBlocks(
			lastEvent.block,
			this.UNDO_REVERSE_ACTIONS[lastEvent.action],
			BlockChangeSource.UNDO,
		);

		this.focusBlockById(lastEvent.block.id);
	}

	@HostListener('document:keydown.shift.control.z', ['$event'])
	@HostListener('document:keydown.shift.meta.z', ['$event'])
	private onRedo(event: KeyboardEvent) {
		if (this.readonly()) return;
		event.preventDefault();
		if (!this.redo_().length) return;
		const redoEvent = this.redo_().at(-1);
		this.redo_.update((h) => h.slice(0, -1));
		if (!redoEvent) return;
		this.updateBlocks(
			redoEvent.block,
			redoEvent.action,
			BlockChangeSource.REDO,
		);

		this.focusBlockById(redoEvent.block.id);
	}

	@HostListener('document:keydown.control.v', ['$event'])
	@HostListener('document:keydown.meta.v', ['$event'])
	private onPasteBlock(event: KeyboardEvent) {
		if (this.readonly()) return;
		event.stopPropagation();

		const copiedBlock = this.copyBlock_();
		if (!copiedBlock) return;

		const pageBlocks = this.eSigningBlocks_().filter(
			(block) => block.envelopePageNumber === copiedBlock.envelopePageNumber,
		);
		const collisionFreePosition = this.findNonCollidingPosition(
			pageBlocks,
			copiedBlock.x,
			copiedBlock.y,
		);

		const { x: finalX, y: finalY } = this.applyPageMargin(
			collisionFreePosition.x,
			collisionFreePosition.y,
			copiedBlock.width,
			copiedBlock.height,
			copiedBlock.canvasWidth,
			copiedBlock.canvasHeight,
		);

		const newBlockId = createId();
		const newBlock: ESigningBlock = {
			...copiedBlock,
			id: newBlockId,
			x: finalX,
			y: finalY,
		};

		this.updateBlocks(newBlock, ActionType.ADD, BlockChangeSource.ADD);

		this.focusBlockById(newBlockId);
	}

	@HostListener('mousewheel', ['$event'])
	private onMouseWheel(event: WheelEvent) {
		if (!event.ctrlKey && !event.metaKey) return;
		event.preventDefault();
		this.zoom(
			-Math.sign(event.deltaY || -event.detail),
			(overflowElement, scaleRatio) => {
				const rect = overflowElement?.getBoundingClientRect();
				const mouseX = event.clientX - (rect?.left ?? 0);
				const mouseY = event.clientY - (rect?.top ?? 0);
				const scrollLeft = overflowElement.scrollLeft;
				const scrollTop = overflowElement.scrollTop;
				const newScrollLeft = (mouseX + scrollLeft) * scaleRatio - mouseX;
				const newScrollTop = (mouseY + scrollTop) * scaleRatio - mouseY;
				return {
					left: newScrollLeft,
					top: newScrollTop,
				};
			},
		);

		this.updateAndValidateScale((currentScale) => {
			const delta = -Math.sign(event.deltaY || -event.detail); // Normalize delta to -1, 0, or 1, scrolling up is -1, down is 1
			const zoomFactor = 0.05;
			const newScale = currentScale + delta * zoomFactor;

			const overflowElement = this.pdfContainer_()?.nativeElement;
			if (overflowElement) {
				const rect = overflowElement?.getBoundingClientRect();
				const mouseX = event.clientX - (rect?.left ?? 0);
				const mouseY = event.clientY - (rect?.top ?? 0);
				const scaleRatio = newScale / currentScale;
				const scrollLeft = overflowElement.scrollLeft;
				const scrollTop = overflowElement.scrollTop;
				const newScrollLeft = (mouseX + scrollLeft) * scaleRatio - mouseX;
				const newScrollTop = (mouseY + scrollTop) * scaleRatio - mouseY;
				overflowElement.scrollTo({
					left: newScrollLeft,
					top: newScrollTop,
					behavior: 'instant',
				});
			}
			return newScale;
		});
	}

	constructor() {
		effect(() => this.eSigningBlocks_.set(this.eSigningBlocks()));

		effect(() =>
			this.setInitialScale(
				this.maxPageWidth_(),
				this.pdfContainer_()?.nativeElement?.clientWidth,
			),
		);
	}

	onDeleteBlock(event: KeyboardEvent, block: ESigningBlock) {
		if (this.readonly()) return;
		event.stopPropagation();
		this.updateBlocks(block, ActionType.DELETE, BlockChangeSource.DELETE);
	}

	onCopyBlock(event: KeyboardEvent, block: ESigningBlock) {
		if (this.readonly()) return;
		event.stopPropagation();
		this.copyBlock_.set(block);
	}

	onBlockValueChange(block: ESigningBlock, newValue: string) {
		if (this.readonly()) return;
		const updatedBlock = { ...block, textDefaultValue: newValue };
		this.updateBlocks(updatedBlock, ActionType.UPDATE, BlockChangeSource.INPUT);
	}

	onBlockFocus(blockId: string) {
		this.focusedBlockId_.set(blockId);
	}

	onBlockBlur() {
		this.focusedBlockId_.set(null);
	}

	onBlockEditingStateChange(blockId: string, isEditing: boolean) {
		if (isEditing) {
			this.editingBlockId_.set(blockId);
		} else {
			this.editingBlockId_.set(null);
		}
	}

	onPageClick(event: MouseEvent, pageNum: number) {
		if (this.readonly() || !this.pendingBlock()) return;

		const target = event.target;
		if (!target || !(target instanceof HTMLImageElement)) return;

		this.createBlockAtPosition(event, target, this.pendingBlock(), pageNum);
	}

	onDragOver(event: DragEvent) {
		if (this.readonly()) return;
		event.preventDefault();
		if (this.draggedBlock_()) {
			event.dataTransfer.dropEffect = 'move';
			return;
		}

		if (this.pendingDragBlock()) {
			event.dataTransfer.dropEffect = 'copy';
		}
	}

	onResizeStart(
		event: MouseEvent,
		prevBlock: ESigningBlock,
		corner:
			| 'top-left'
			| 'top-right'
			| 'bottom-left'
			| 'bottom-right' = 'bottom-right',
	) {
		if (this.readonly()) return;
		event.preventDefault();
		const action = ActionType.UPDATE;
		const startX = event.clientX;
		const startY = event.clientY;
		const origX = prevBlock.x;
		const origY = prevBlock.y;
		const origWidth = prevBlock.width;
		const origHeight = prevBlock.height;
		const MAX_WIDTH = prevBlock.canvasWidth - prevBlock.x;
		const MAX_HEIGHT = prevBlock.canvasHeight - prevBlock.y;
		let newBlock: ESigningBlock;

		const onMouseMove = (moveEvent: MouseEvent) => {
			const scale = this.scale_();
			const deltaX = (moveEvent.clientX - startX) / scale;
			const deltaY = (moveEvent.clientY - startY) / scale;

			const resizeMap = {
				'top-left': {
					x: Math.max(0, origX + deltaX),
					y: Math.max(0, origY + deltaY),
					width: (() => {
						const newX = Math.max(0, origX + deltaX);
						const actualDeltaX = newX - origX;
						const maxAllowedWidth = prevBlock.canvasWidth - newX;
						return Math.min(
							Math.max(this.MIN_BLOCK_WIDTH, origWidth - actualDeltaX),
							maxAllowedWidth,
						);
					})(),
					height: (() => {
						const newY = Math.max(0, origY + deltaY);
						const actualDeltaY = newY - origY;
						const maxAllowedHeight = prevBlock.canvasHeight - newY;
						return Math.min(
							Math.max(this.MIN_BLOCK_HEIGHT, origHeight - actualDeltaY),
							maxAllowedHeight,
						);
					})(),
				},
				'top-right': {
					y: Math.max(0, origY + deltaY),
					width: Math.min(
						Math.max(this.MIN_BLOCK_WIDTH, origWidth + deltaX),
						MAX_WIDTH,
					),
					height: (() => {
						const newY = Math.max(0, origY + deltaY);
						const actualDeltaY = newY - origY;
						const maxAllowedHeight = prevBlock.canvasHeight - newY;
						return Math.min(
							Math.max(this.MIN_BLOCK_HEIGHT, origHeight - actualDeltaY),
							maxAllowedHeight,
						);
					})(),
				},
				'bottom-left': {
					x: Math.max(0, origX + deltaX),
					width: (() => {
						const newX = Math.max(0, origX + deltaX);
						const actualDeltaX = newX - origX;
						const maxAllowedWidth = prevBlock.canvasWidth - newX;
						return Math.min(
							Math.max(this.MIN_BLOCK_WIDTH, origWidth - actualDeltaX),
							maxAllowedWidth,
						);
					})(),
					height: Math.min(
						Math.max(this.MIN_BLOCK_HEIGHT, origHeight + deltaY),
						MAX_HEIGHT,
					),
				},
				'bottom-right': {
					width: Math.min(
						Math.max(this.MIN_BLOCK_WIDTH, origWidth + deltaX),
						MAX_WIDTH,
					),
					height: Math.min(
						Math.max(this.MIN_BLOCK_HEIGHT, origHeight + deltaY),
						MAX_HEIGHT,
					),
				},
			};

			newBlock = { ...prevBlock, ...resizeMap[corner] };

			this.updateBlocksDisplay(action, newBlock);
		};

		const onMouseUp = () => {
			if (this.validateBlockDimensions(newBlock)) {
				this.addToHistory(prevBlock, action);
				this.emitBlockChange(newBlock, action);
			}
			document.removeEventListener('mousemove', onMouseMove);
			document.removeEventListener('mouseup', onMouseUp);
		};

		document.addEventListener('mousemove', onMouseMove);
		document.addEventListener('mouseup', onMouseUp);
	}

	onDrop(dropZoneElEvent: DragEvent, pageNum: number) {
		if (this.readonly()) return;

		const pageImage = this.imageChildren_().find((ref) => {
			const el = ref.nativeElement;
			return el.getAttribute('data-page') === String(pageNum);
		})?.nativeElement;

		if (!pageImage) return;

		const pendingDrag = this.pendingDragBlock();
		if (pendingDrag) {
			this.createBlockAtPosition(
				dropZoneElEvent,
				pageImage,
				pendingDrag,
				pageNum,
			);
			return;
		}

		const { event: dropElEvent, block } = this.draggedBlock_() ?? {};
		if (!dropElEvent || !block) return;

		const { top, left } = this.calculateDeltaPosition({
			scale: this.scale_(),
			dropElEvent,
			dropZoneElEvent,
			pageImage,
		});

		const pageMetadata = this.pages().find(
			(page) => page.documentPageNumber === pageNum,
		);

		const canvasWidth = pageMetadata?.width ?? block.canvasWidth;
		const canvasHeight = pageMetadata?.height ?? block.canvasHeight;

		const { x: finalX, y: finalY } = this.applyPageMargin(
			left,
			top,
			block.width,
			block.height,
			canvasWidth,
			canvasHeight,
		);

		this.updateBlocks(
			{
				...block,
				y: finalY,
				x: finalX,
				envelopePageNumber: pageNum,
				canvasHeight,
				canvasWidth,
			},
			ActionType.UPDATE,
			BlockChangeSource.MOVE,
		);
	}

	private createBlockAtPosition(
		event: { clientX: number; clientY: number },
		pageElement: HTMLImageElement,
		pendingBlock: PushBlock,
		pageNum: number,
	): void {
		const scale = this.scale_();
		const rect = pageElement.getBoundingClientRect();

		const relativeX = event.clientX - rect.left;
		const relativeY = event.clientY - rect.top;

		const x = relativeX / scale;
		const y = relativeY / scale;

		const pageMetadata = this.pages().find(
			(page) => page.documentPageNumber === pageNum,
		);

		const canvasWidth = pageMetadata?.width ?? pageElement.clientWidth / scale;
		const canvasHeight =
			pageMetadata?.height ?? pageElement.clientHeight / scale;

		const { x: finalX, y: finalY } = this.applyPageMargin(
			x,
			y,
			pendingBlock.width,
			pendingBlock.height,
			canvasWidth,
			canvasHeight,
		);

		const newBlockId = createId();

		this.updateBlocks(
			{
				...pendingBlock,
				x: finalX,
				y: finalY,
				envelopePageNumber: pageNum,
				id: newBlockId,
				canvasWidth,
				canvasHeight,
				canvasDpi:
					pageMetadata?.dpi ?? this.pages().find((page) => !!page.dpi)?.dpi,
				isPretagged: false,
			},
			ActionType.ADD,
			BlockChangeSource.ADD,
		);

		this.blockPlaced.emit();
		this.focusBlockById(newBlockId);
	}

	public setActiveDraggedBlock(event: DragEvent, block?: ESigningBlock) {
		if (
			this.readonly() ||
			!event.target ||
			!(event.target instanceof HTMLButtonElement)
		)
			return;
		this.draggedBlock_.set(block ? { event, block } : null);
	}

	public zoomIn() {
		this.zoomViaButtons(true);
	}

	public zoomOut() {
		this.zoomViaButtons(false);
	}

	public setScale(percentage: number) {
		this.setAndValidateScale(percentage / 100);
	}

	private zoom(
		delta: number,
		cb: (
			overflowElement: HTMLElement,
			scaleRatio: number,
		) => { left: number; top: number },
	) {
		this.updateAndValidateScale((currentScale) => {
			const newScale = currentScale + delta * 0.05;
			const scaleRatio = newScale / currentScale;

			const overflowElement = this.pdfContainer_()?.nativeElement;
			if (overflowElement) {
				overflowElement.scrollTo({
					...cb(overflowElement, scaleRatio),
					behavior: 'instant',
				});
			}

			return newScale;
		});
	}

	private zoomViaButtons(isZoomIn: boolean) {
		this.zoom(isZoomIn ? 1 : -1, (overflowElement, scaleRatio) => {
			const scrollLeft = overflowElement.scrollLeft;
			const scrollTop = overflowElement.scrollTop;
			const centerX = scrollLeft + overflowElement.clientWidth / 2;
			const centerY = scrollTop + overflowElement.clientHeight / 2;

			const newCenterX = centerX * scaleRatio;
			const newCenterY = centerY * scaleRatio;

			return {
				left: newCenterX - overflowElement.clientWidth / 2,
				top: newCenterY - overflowElement.clientHeight / 2,
			};
		});
	}

	private updateBlocks(
		updatedBlock: ESigningBlock,
		action: ActionType,
		blockChangeSource: BlockChangeSource,
	) {
		if (!this.validateBlockDimensions(updatedBlock)) {
			return;
		}
		if (BlockChangeSource.UNDO !== blockChangeSource) {
			this.updateHistory(updatedBlock, action);
		}
		if (
			![BlockChangeSource.REDO, BlockChangeSource.UNDO].includes(
				blockChangeSource,
			)
		) {
			this.redo_.set([]);
		}

		this.emitBlockChange(updatedBlock, action);
		this.updateBlocksDisplay(action, updatedBlock);
	}

	private emitBlockChange(updatedBlock: ESigningBlock, action: ActionType) {
		this.eSigningBlockChange.emit({ block: updatedBlock, action });
	}

	private validateBlockDimensions(block: ESigningBlock): boolean {
		if (!block) return false;
		const { width, height, x, y, canvasWidth, canvasHeight } = block;
		if (
			[width, height, x, y, canvasWidth, canvasHeight].some((v) =>
				Number.isNaN(v),
			)
		) {
			return false;
		}
		return (
			width >= this.MIN_BLOCK_WIDTH &&
			height >= this.MIN_BLOCK_HEIGHT &&
			x >= 0 &&
			y >= 0 &&
			x + width <= canvasWidth &&
			y + height <= canvasHeight
		);
	}

	private updateBlocksDisplay(action: ActionType, updatedBlock: ESigningBlock) {
		this.eSigningBlocks_.update((blocks) => {
			switch (action) {
				case ActionType.DELETE:
					return blocks.filter((b) => b.id !== updatedBlock.id);
				case ActionType.UPDATE:
					return blocks.map((b) =>
						b.id === updatedBlock.id ? updatedBlock : b,
					);
				case ActionType.ADD:
					return blocks.concat(updatedBlock);
				default:
					throw new Error(`Unknown action type: ${action}`);
			}
		});
	}

	private setInitialScale(maxPageWidth: number, maxWidth = 600) {
		if (!maxPageWidth || maxPageWidth <= 0) return;
		this.setAndValidateScale(maxWidth / maxPageWidth / 1.1); // 90% of max width
	}

	private updateHistory(updatedBlock: ESigningBlock, action: ActionType) {
		this.addToHistory(
			action === ActionType.ADD
				? updatedBlock
				: this.eSigningBlocks_().find((b) => b.id === updatedBlock.id),
			action,
		);
	}

	private setPages = (
		pages: PdfPageDetail[],
		signingBlocks: ESigningBlock[],
	): HolisticPdfPageDetail[] => {
		if (!pages?.length) return [];
		return pages.map((page) => ({
			...page,
			blocks: signingBlocks
				.filter((block) => block.envelopePageNumber === page.documentPageNumber)
				.map((block) => ({
					...block,
					showWidth: Math.max(
						this.MIN_BLOCK_WIDTH,
						block.width * this.scale_(),
					),
					showHeight: Math.max(
						this.MIN_BLOCK_HEIGHT,
						block.height * this.scale_(),
					),
				})),
		}));
	};

	private addToHistory(current: ESigningBlock, action: ActionType) {
		this.history_.update((h) => h.concat({ block: current, action: action }));
	}

	private calculateDeltaPosition({
		scale,
		dropElEvent,
		dropZoneElEvent,
		pageImage,
	}: {
		scale: number;
		dropElEvent: DragEvent;
		dropZoneElEvent: DragEvent;
		pageImage: HTMLImageElement;
	}) {
		if (
			!dropElEvent ||
			!(dropElEvent.target instanceof HTMLElement) ||
			!dropZoneElEvent ||
			!pageImage
		)
			return { top: 0, left: 0 };

		const imageRect = pageImage.getBoundingClientRect();

		const dragOffsetX = dropElEvent.offsetX;
		const dragOffsetY = dropElEvent.offsetY;

		const dropX = dropZoneElEvent.clientX - imageRect.left;
		const dropY = dropZoneElEvent.clientY - imageRect.top;

		const left = (dropX - dragOffsetX) / scale;
		const top = (dropY - dragOffsetY) / scale;

		return { top, left };
	}

	private addNewBlock(newBlock: PushBlock) {
		this.addBlockToClosestPage(
			newBlock,
			this.imageChildren_().map((ref) => ref.nativeElement),
			this.scale_(),
		);
	}

	private findNonCollidingPosition(
		blocks: ESigningBlock[],
		x: number,
		y: number,
		offset = this.COLLISION_OFFSET,
		tolerance = this.COLLISION_TOLERANCE,
	): { x: number; y: number } {
		const hasCollision = blocks.some(
			(block) =>
				Math.abs(block.x - x) < tolerance && Math.abs(block.y - y) < tolerance,
		);

		return hasCollision
			? this.findNonCollidingPosition(
					blocks,
					x + offset,
					y - offset,
					offset,
					tolerance,
				)
			: { x, y };
	}

	private applyPageMargin(
		x: number,
		y: number,
		blockWidth: number,
		blockHeight: number,
		canvasWidth: number,
		canvasHeight: number,
	): { x: number; y: number } {
		const clampedX = Math.max(
			this.PAGE_MARGIN,
			Math.min(x, canvasWidth - blockWidth - this.PAGE_MARGIN),
		);
		const clampedY = Math.max(
			this.PAGE_MARGIN,
			Math.min(y, canvasHeight - blockHeight - this.PAGE_MARGIN),
		);

		return { x: clampedX, y: clampedY };
	}

	private addBlockToClosestPage(
		addBlock: PushBlock | null,
		images: HTMLImageElement[],
		scale = 1,
	) {
		if (!addBlock || !images.length) return;

		const observer = new IntersectionObserver(
			(entries) => {
				const mostVisibleImg = entries.sort(
					(a, b) => b.intersectionRatio - a.intersectionRatio,
				)[0]?.target as HTMLImageElement;

				if (mostVisibleImg) {
					const container = this.pdfContainer_()?.nativeElement;
					if (!container) return;

					const containerRect = container.getBoundingClientRect();
					const imageRect = mostVisibleImg.getBoundingClientRect();

					const visibleLeft = Math.max(imageRect.left, containerRect.left);
					const visibleTop = Math.max(imageRect.top, containerRect.top);
					const visibleRight = Math.min(imageRect.right, containerRect.right);
					const visibleBottom = Math.min(
						imageRect.bottom,
						containerRect.bottom,
					);

					const viewportCenterX = (visibleLeft + visibleRight) / 2;
					const viewportCenterY = (visibleTop + visibleBottom) / 2;

					const imageRelativeX = viewportCenterX - imageRect.left;
					const imageRelativeY = viewportCenterY - imageRect.top;

					const left = (imageRelativeX - (addBlock.width * scale) / 2) / scale;
					const top = (imageRelativeY - (addBlock.height * scale) / 2) / scale;

					const pageNumber = Number(
						mostVisibleImg.getAttribute('data-page') ?? '1',
					);
					const pageMetadata = this.pages().find(
						(page) => page.documentPageNumber === pageNumber,
					);

					const pageBlocks = this.eSigningBlocks_().filter(
						(block) => block.envelopePageNumber === pageNumber,
					);
					const collisionFreePosition = this.findNonCollidingPosition(
						pageBlocks,
						left,
						top,
					);

					const canvasWidth =
						pageMetadata?.width ?? mostVisibleImg.clientWidth / scale;
					const canvasHeight =
						pageMetadata?.height ?? mostVisibleImg.clientHeight / scale;

					const { x: finalX, y: finalY } = this.applyPageMargin(
						collisionFreePosition.x,
						collisionFreePosition.y,
						addBlock.width,
						addBlock.height,
						canvasWidth,
						canvasHeight,
					);

					const newBlockId = createId();

					this.updateBlocks(
						{
							...addBlock,
							x: finalX,
							y: finalY,
							width: addBlock.width,
							height: addBlock.height,
							envelopePageNumber: pageNumber,
							id: newBlockId,
							canvasWidth,
							canvasHeight,
							canvasDpi:
								pageMetadata?.dpi ??
								this.pages().find((page) => !!page.dpi)?.dpi,
							isPretagged: false,
						},
						ActionType.ADD,
						BlockChangeSource.ADD,
					);

					this.focusBlockById(newBlockId);
				}

				observer.disconnect();
			},
			{
				threshold: Array.from({ length: 101 }, (_, i) => i / 100),
			},
		);

		images.forEach((img) => observer.observe(img));
	}

	private updateAndValidateScale(cb: (prevValue: number) => number) {
		this.scale_.update((prev) => this.limitScaleRange(cb(prev)));
	}

	private setAndValidateScale(value: number) {
		this.scale_.set(this.limitScaleRange(value));
	}

	private limitScaleRange(newScale: number): number {
		return Math.max(this.MIN_SCALE, Math.min(newScale, this.MAX_SCALE));
	}

	private focusBlockById(blockId: string): void {
		setTimeout(() => {
			const blockElement = document.querySelector(
				`[data-block-id="${blockId}"] button`,
			) as HTMLElement;
			if (blockElement) {
				blockElement.focus();
			}
		}, 0);
	}
}
